import { forwardRef, useImperativeHandle, useRef, useState } from "react"
import styled from "styled-components"

const ChartTooltipWrapper = styled.div<{ $visible: boolean }>`
  position: absolute;
  background: var(--surface-1);
  border-radius: 8px;
  color: var(--text-primary);
  pointer-events: none;
  transition: opacity 0.1s ease;
  padding: 4px;
  border: 1px solid var(--border-subtle);
  font-size: 14px;
  z-index: 1100;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
`

const ChartTooltipTitle = styled.div`
  padding-bottom: 4px;
  font-weight: 600;
  font-size: 12px;
  color: var(--text-muted);
`

const ChartTooltipRow = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 5px;
`

const ChartTooltipColorBar = styled.span<{ $color: string }>`
  background: ${({ $color }) => $color};
  border-color: ${({ $color }) => $color};
  border-width: 2px;
  margin-right: 5px;
  height: 20px;
  width: 4px;
  display: inline-block;
`

export interface TooltipRow {
  color: string
  label: string
  value: string
}

export interface ChartTooltipHandle {
  update: (data: {
    visible: boolean
    title: string
    rows: TooltipRow[]
    left: number
    top: number | null
    bottom: number | null
    transform: string
    width: number
  }) => void
  getHeight: () => number
}

const ChartTooltipOverlay = forwardRef<ChartTooltipHandle>((_props, ref) => {
  const wrapperRef = useRef<HTMLDivElement>(null)
  const [data, setData] = useState<{
    visible: boolean
    title: string
    rows: TooltipRow[]
    left: number
    top: number | null
    bottom: number | null
    transform: string
    width: number
  }>({ visible: false, title: "", rows: [], left: 0, top: 0, bottom: null, transform: "", width: 220 })

  useImperativeHandle(ref, () => ({
    update: setData,
    getHeight: () => wrapperRef.current?.offsetHeight ?? 80,
  }))

  return (
    <ChartTooltipWrapper
      ref={wrapperRef}
      $visible={data.visible}
      style={{
        width: data.width,
        left: data.left,
        top: data.top ?? "auto",
        bottom: data.bottom ?? "auto",
        transform: data.transform,
      }}
    >
      {data.title && <ChartTooltipTitle>{data.title}</ChartTooltipTitle>}
      {data.rows.map((row, i) => (
        <ChartTooltipRow key={i}>
          <ChartTooltipColorBar $color={row.color} />
          {row.label ? `${row.label}: ${row.value}` : row.value}
        </ChartTooltipRow>
      ))}
    </ChartTooltipWrapper>
  )
})

ChartTooltipOverlay.displayName = "ChartTooltipOverlay"

export default ChartTooltipOverlay
